import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { AUTH_ACTION_TYPE, AuthAction, AuthSelector, CoreState, ResponsibleGamingConfigService } from "@obg/ngx.core";
import { fromMillisecondsToMinutes, fromMinutesToMilliseconds } from "@obg/ngx.global";
import { PlatformService } from "@obg/ngx.global.evo";
import { isEmpty } from "lodash-es";
import { from, Observable, of, Subject, timer } from "rxjs";
import { concatAll, concatMap, filter, map, switchMap, takeUntil, tap, withLatestFrom } from "rxjs/operators";

import { MatNotificationsService } from "../mat-notifications/mat-notifications.service";
import { RealTimeNotificationsAction } from "../real-time-notifications/real-time-notifications.action";
import { RealTimeNotificationsConfig } from "../real-time-notifications/real-time-notifications.config";
import { RealtimeNotificationsFeatureState } from "../state";

import { NotificationResultRedirectAction } from "./notification-result/notification-result.model";
import { RgInteractionsAction } from "./rg-interactions.action";
import {
	RG_INTERACTION_PERIOD_DAY,
	RG_INTERACTION_PERIOD_NIGHT,
	RG_INTERACTIONS_ALERT_TIME_ID,
} from "./rg-interactions.const";
import { RgInteractionsRealTimePaylod, RgInteractionType } from "./rg-interactions.model";
import { RgInteractionsSelector } from "./rg-interactions.selector";

@Injectable()
export class RgInteractionsEffect {
	timeTriggerNotifications$ = this.platformService.forClient(() =>
		createEffect(() =>
			this.actions$.pipe(
				ofType(AUTH_ACTION_TYPE.sessionStart),
				filter(() => this.configService.getInteractionsEnabled()),
				concatMap((action) => of(action).pipe(withLatestFrom(this.store.select(AuthSelector.getState)))),
				map(([, state]) => state?.sessionStartTimeInMillis),
				switchMap((sessionStartTime) =>
					timer(0, fromMinutesToMilliseconds(1)).pipe(
						filter(() => !!sessionStartTime && this.shouldShowInteraction(sessionStartTime)),
						map(() =>
							RgInteractionsAction.verifyInteraction({
								id: RG_INTERACTIONS_ALERT_TIME_ID,
								type: RgInteractionType.Time,
								redirectButtonText: "rg-interactions.logout",
								redirectButtonAction: this.authAction.logout(),
								notificationResultAction: NotificationResultRedirectAction.Logout,
							})
						),
						takeUntil(this.actions$.pipe(ofType(AUTH_ACTION_TYPE.sessionEnd)))
					)
				)
			)
		)
	);

	notOpenedAlert$ = createEffect(() =>
		this.actions$.pipe(
			ofType(AUTH_ACTION_TYPE.sessionStart),
			filter(() => this.configService.getInteractionsEnabled()),
			withLatestFrom(this.store.select(this.selector.getNotOpenedAlerts())),
			filter(([, notOpenedAlerts]) => !isEmpty(notOpenedAlerts)),
			switchMap(([, notOpenedAlerts]) => from(notOpenedAlerts)),
			map((notOpenedAlert) => RgInteractionsAction.addPopupToQueue(notOpenedAlert))
		)
	);

	openAlertsInQueue$ = createEffect(() =>
		this.actions$.pipe(
			ofType(AUTH_ACTION_TYPE.sessionStart),
			switchMap(() => this.alerts$.pipe(map((alert) => RgInteractionsAction.closePopup({ ...alert }))))
		)
	);

	verifyInteraction$ = createEffect(() =>
		this.actions$.pipe(
			ofType(RgInteractionsAction.verifyInteraction),
			map(({ alert }) => alert),
			withLatestFrom(this.store.select(this.selector.getNotOpenedAlerts())),
			filter(([alert, notOpenedAlerts]) => !notOpenedAlerts.find((x) => x.id === alert.id)),
			map(([alert]) => RgInteractionsAction.addPopupToQueue(alert))
		)
	);

	addAlertsToQueue$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(RgInteractionsAction.addPopupToQueue),
				map(({ alert }) => alert),
				tap((alert) => this.alertsSubject$.next(this.addPopupToQueueFunc(alert)))
			),
		{ dispatch: false }
	);

	lossTriggerNotifications$ = createEffect(() =>
		this.actions$.pipe(
			ofType(RealTimeNotificationsAction.connectionSuccess),
			filter(() => this.realTimeNotificationsConfig.getLossTriggerEnabled()),
			map(() =>
				RealTimeNotificationsAction.subscribeToChannel(this.realTimeNotificationsConfig.getLossTriggerTopic())
			)
		)
	);

	wageringTriggerNotifications$ = createEffect(() =>
		this.actions$.pipe(
			ofType(RealTimeNotificationsAction.connectionSuccess),
			filter(() => this.realTimeNotificationsConfig.getWageringTriggerEnabled()),
			map(() =>
				RealTimeNotificationsAction.subscribeToChannel(
					this.realTimeNotificationsConfig.getWageringTriggerTopic()
				)
			)
		)
	);

	depositTriggerNotifications$ = createEffect(() =>
		this.actions$.pipe(
			ofType(RealTimeNotificationsAction.connectionSuccess),
			filter(() => this.realTimeNotificationsConfig.getDepositTriggerEnabled()),
			map(() =>
				RealTimeNotificationsAction.subscribeToChannel(
					this.realTimeNotificationsConfig.getDepositTriggerTopic()
				)
			)
		)
	);

	customerMadeDeposit$ = createEffect(() =>
		this.actions$.pipe(
			ofType(RealTimeNotificationsAction.connectionSuccess),
			filter(() => this.realTimeNotificationsConfig.getCustomerMadeDepositEnabled()),
			map(() =>
				RealTimeNotificationsAction.subscribeToChannel(
					this.realTimeNotificationsConfig.getCustomerMadeDepositTopic()
				)
			)
		)
	);

	toolsTriggerNotifications$ = createEffect(() =>
		this.actions$.pipe(
			ofType(RealTimeNotificationsAction.connectionSuccess),
			filter(() => this.realTimeNotificationsConfig.getToolsTriggerEnabled()),
			map(() =>
				RealTimeNotificationsAction.subscribeToChannel(this.realTimeNotificationsConfig.getToolsTriggerTopic())
			)
		)
	);

	// This is temporary location, and it will be changed in future after kyc will be moved to common
	kycActionNotifications$ = createEffect(() =>
		this.actions$.pipe(
			ofType(RealTimeNotificationsAction.connectionSuccess),
			filter(() => this.realTimeNotificationsConfig.getKycTriggerEnabled()),
			map(() =>
				RealTimeNotificationsAction.subscribeToChannel(this.realTimeNotificationsConfig.getKycTriggerTopic())
			)
		)
	);

	customerActionsNotifications$ = createEffect(() =>
		this.actions$.pipe(
			ofType(RealTimeNotificationsAction.connectionSuccess),
			filter(() => this.realTimeNotificationsConfig.getCustomerActionsEnabled()),
			map(() =>
				RealTimeNotificationsAction.subscribeToChannel(
					this.realTimeNotificationsConfig.getCustomerActionsTopic()
				)
			)
		)
	);

	balanceLimitExceededNotifications$ = createEffect(() =>
		this.actions$.pipe(
			ofType(RealTimeNotificationsAction.connectionSuccess),
			filter(() => this.realTimeNotificationsConfig.getBalanceLimitExceededEnabled()),
			map(() =>
				RealTimeNotificationsAction.subscribeToChannel(
					this.realTimeNotificationsConfig.getBalanceLimitExceededTopic()
				)
			)
		)
	);

	depositLimitReachedNotifications$ = createEffect(() =>
		this.actions$.pipe(
			ofType(RealTimeNotificationsAction.connectionSuccess),
			filter(() => this.realTimeNotificationsConfig.getDepositLimitReachedEnabled()),
			map(() =>
				RealTimeNotificationsAction.subscribeToChannel(
					this.realTimeNotificationsConfig.getDepositLimitReachedTopic()
				)
			)
		)
	);

	sessionLimitReachedNotifications$ = createEffect(() =>
		this.actions$.pipe(
			ofType(RealTimeNotificationsAction.connectionSuccess),
			filter(() => this.realTimeNotificationsConfig.getSessionLimitReachedEnabled()),
			map(() =>
				RealTimeNotificationsAction.subscribeToChannel(
					this.realTimeNotificationsConfig.getSessionLimitReachedTopic()
				)
			)
		)
	);

	private alertsSubject$: Subject<Observable<RgInteractionsRealTimePaylod>> = new Subject();
	private alerts$: Observable<RgInteractionsRealTimePaylod>;

	constructor(
		private actions$: Actions,
		private store: Store<CoreState & RealtimeNotificationsFeatureState>,
		private selector: RgInteractionsSelector,
		private platformService: PlatformService,
		private configService: ResponsibleGamingConfigService,
		private notificationsService: MatNotificationsService,
		private realTimeNotificationsConfig: RealTimeNotificationsConfig,
		private authAction: AuthAction
	) {
		this.alerts$ = this.alertsSubject$.pipe(concatAll());
	}

	private addPopupToQueueFunc(alert: RgInteractionsRealTimePaylod) {
		return this.notificationsService
			.openRgAlert(
				alert.type,
				alert.redirectButtonText,
				alert.redirectButtonAction,
				alert.notificationResultAction
			)
			.pipe(
				switchMap((x) => x.afterClosed()),
				map(() => alert)
			);
	}

	private shouldShowInteraction(sessionStartTime: number) {
		const currentHourInUK = parseInt(
			new Date().toLocaleTimeString("en-GB", { timeZone: "Europe/London", hour: "2-digit" }),
			10
		);
		const sessionTimeInMinutes = Math.floor(fromMillisecondsToMinutes(Date.now() - sessionStartTime));
		const interactionPeriod =
			currentHourInUK > 8 && currentHourInUK < 22 ? RG_INTERACTION_PERIOD_DAY : RG_INTERACTION_PERIOD_NIGHT;
		return !!sessionTimeInMinutes && sessionTimeInMinutes % interactionPeriod === 0;
	}
}
